import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiRequestService, LogService, UseCase } from '@prekog/common-ui';
import { catchError, map, Observable, of, switchMap, timeout } from 'rxjs';
import { STORE_BACKEND_URL } from '../app.config';
import { GetRandomCategoryProductRequest } from './getrandomcategoryproductrequest';
import { GetRandomCategoryProductResponse } from './getrandomcategoryproductresponse';
import { GetRandomPackageProductRequest } from './getrandompackageproductrequest';
import { GetRandomPackageProductResponse } from './getrandompackageproductresponse';
import { Material } from '../shared/material.model';

export class GetPackageOfferOrFallbackRequest {
    productId?: number;
}

@Injectable({
    providedIn: 'root'
})
export class GetPackageOfferOrFallbackUseCase extends UseCase<GetPackageOfferOrFallbackRequest, Observable<Material | undefined>> {
    private readonly _apiRequestService = inject(ApiRequestService);
    private readonly _storeBackendUrl = inject<string>(STORE_BACKEND_URL);

    constructor(
        _logService: LogService,
        protected override _http: HttpClient
    ) {
        super(_logService, _http);
    }

    protected override executeBusinessLogic(
        request: GetPackageOfferOrFallbackRequest | void
    ): Observable<Material | undefined> {
        const productId = request?.productId;

        const getRandomPackageRequest: GetRandomPackageProductRequest = {
            productId: productId
        };

        return this._apiRequestService
            .sendRequest<GetRandomPackageProductRequest, GetRandomPackageProductResponse>(
                this._storeBackendUrl,
                getRandomPackageRequest,
                true,
                '/store/get-random-package'
            )
            .pipe(
                timeout(10000),
                switchMap((response: GetRandomPackageProductResponse) => {
                    const pkg = response?.packageObject;
                    if (pkg && pkg.packageName && pkg.packageName.trim().length > 0) {
                        const packagePrice = response.packagePrice ?? pkg.packagePrice;
                        if (packagePrice && packagePrice > 0) {
                            const material: Material = {
                                title: pkg.packageName + ' csomag',
                                originalPrice: packagePrice,
                                oldPrice: response.packageOriginalPrice,
                                url: pkg.imageUrl,
                                routerLink: pkg.routerLink,
                                packageName: pkg.packageName,
                                quantityOfSupply: 1
                            };
                            if (response.packageOriginalPrice && response.packageOriginalPrice > packagePrice) {
                                material.discountPriceActive = true;
                                material.typeOfOriginalPrice = 'Külön-külön:';
                                material.typeOfPrice = 'Akciós ár:';
                            }
                            return of(material);
                        }
                    }
                    return this._getRandomCategoryProduct(productId);
                }),
                catchError(() => {
                    return this._getRandomCategoryProduct(productId);
                })
            );
    }

    private _getRandomCategoryProduct(productId: number | undefined): Observable<Material | undefined> {
        const request: GetRandomCategoryProductRequest = { productId };

        return this._apiRequestService
            .sendRequest<GetRandomCategoryProductRequest, GetRandomCategoryProductResponse>(
                this._storeBackendUrl,
                request,
                true,
                '/store/getRandomCategoryProduct'
            )
            .pipe(
                timeout(10000),
                map((response: GetRandomCategoryProductResponse) => {
                    const product = response?.product;
                    if (product && product.title && product.title.trim().length > 0 && product.url && product.url.trim().length > 0) {
                        const material: Material = {
                            title: product.title,
                            author: product.author,
                            originalPrice: product.originalPrice,
                            url: product.url,
                            routerLink: product.routerLink,
                            quantityOfSupply: product.quantityOfSupply
                        };
                        return material;
                    }
                    return undefined;
                }),
                catchError(() => {
                    return of(undefined);
                })
            );
    }
}
